/**
 * Trusted first-party context builders for workshop artefacts that live
 * outside the register stores — pre-reading dossiers, run-sequence phases,
 * questionnaires, and AssetLocker output surfaces (transcript, report,
 * analysis, data tables, playback, captures).
 *
 * Injected every chat turn via `buildWorkshopContentContext` (alongside
 * register dumps in `auto-retrieval.ts`). Also consumed by
 * `reference-resolver.ts` for @-mention transclusion.
 */

import { WORKSHOPS, type Workshop } from "@/content/workshops";
import { DOC_BY_WORKSHOP, type DocSection } from "@/features/workshop/modals/doc-content";
import {
  modalContentByWorkshop,
  type QuestionnaireContent,
  type QuestionnaireQuestion,
} from "@/features/workshop/modals/modal-content";
import {
  PHASES_BY_WORKSHOP,
  type WorkshopPhaseSummary,
} from "@/features/run-sequence/run-sequence-content";
import { resolvePhaseObjectives } from "@/features/workshop/overrides/workshop-override-content";
import { getEffectiveWorkshopOverride } from "@/stores/useEffectiveWorkshopOverride";
import { getReport, type ReportContent } from "@/features/ws4-report/report-content";
import { getAnalysis, type AnalysisContent } from "@/features/ws4-analysis/analysis-content";
import { getDataTables, type WorkshopDataTables } from "@/features/ws4-data-tables/data-tables-content";
import { getPlayback, type WorkshopPlayback } from "@/features/ws4-playback/playback-content";
import { getCaptures, type WorkshopCaptures } from "@/features/ws4-captures/captures-content";
import {
  getTranscript,
  type TranscriptBlock,
  type TranscriptTab,
} from "@/features/ws4-transcript/transcript-content";
import { useTranscripts } from "@/stores/useTranscripts";
import { useCaptures } from "@/stores/useCaptures";
import { useGeneratedWorkshopContent } from "@/stores/useGeneratedWorkshopContent";
import { useQuestionnaireResponses } from "@/stores/useQuestionnaireResponses";
import { buildCrewContext } from "./crew-context";
import type { ChatScope } from "./context-builder";
import type { WorkshopOutputId } from "./reference-resolver";

/** Full body included in auto-context / @-mentions up to this length. */
export const BODY_FULL_MAX = 3000;

function truncate(text: string, max = BODY_FULL_MAX): { body: string; truncated: boolean } {
  const trimmed = text.trim();
  if (trimmed.length <= max) return { body: trimmed, truncated: false };
  return {
    body: `${trimmed.slice(0, max).trim()}…`,
    truncated: true,
  };
}

function truncateNote(truncated: boolean): string | null {
  return truncated
    ? `_Body truncated; @-mention for full text or ask about a specific section._`
    : null;
}

function wrapRetrieved(source: string, rows: string[]): string {
  if (rows.length === 0) return "";
  return [
    `<retrieved source="${source}">`,
    rows.join("\n"),
    `</retrieved>`,
  ].join("\n");
}

function wrapSection(heading: string, framing: string, source: string, rows: string[]): string {
  if (rows.length === 0) return "";
  return [
    `## ${heading}`,
    ``,
    framing,
    ``,
    wrapRetrieved(source, rows),
  ].join("\n");
}

function priorWorkshopIds(scope: ChatScope): Workshop["id"][] {
  if (scope === "MC") return [];
  const currentNum = WORKSHOPS.find((w) => w.id === scope)?.num ?? 0;
  return WORKSHOPS.filter((w) => w.num < currentNum).map((w) => w.id);
}

function currentWorkshopIds(scope: ChatScope): Workshop["id"][] {
  if (scope === "MC") return WORKSHOPS.map((w) => w.id);
  return WORKSHOPS.some((w) => w.id === scope) ? [scope] : [];
}

function formatDocSection(section: DocSection): string[] {
  const lines: string[] = [`### ${section.heading}`];
  if (section.intro) lines.push(section.intro);
  const body = section.body;
  if (body) {
    const parts = Array.isArray(body) ? body : [body];
    lines.push(...parts);
  }
  if (section.items?.length) {
    lines.push(...section.items.map((item) => `- ${item}`));
  }
  return lines;
}

/** Serialize a pre-reading dossier to plain text. */
export function formatPreReading(workshopId: Workshop["id"]): string {
  const doc = DOC_BY_WORKSHOP[workshopId];
  if (!doc) return "";
  const lines = [`# ${doc.title}`, ""];
  for (const section of doc.sections) {
    lines.push(...formatDocSection(section), "");
  }
  const { body, truncated } = truncate(lines.join("\n").trim());
  return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
}

/** Phases with facilitator run-sequence overrides applied (matches RunSequence UI). */
export function getEffectiveWorkshopPhases(
  workshopId: Workshop["id"],
): WorkshopPhaseSummary[] {
  const phases = PHASES_BY_WORKSHOP[workshopId] ?? [];
  const override = getEffectiveWorkshopOverride(workshopId);
  return phases.map((p) => ({
    name: p.title,
    objectives: resolvePhaseObjectives(override, p.id, p.objectives ?? []).map(
      (o) => o.text,
    ),
  }));
}

/** Serialize run-sequence phases for one workshop. */
export function formatRunSequence(workshopId: Workshop["id"]): string {
  const phases = getEffectiveWorkshopPhases(workshopId);
  if (phases.length === 0) return "";
  const lines: string[] = [`### Run sequence — ${workshopId}`, ""];
  phases.forEach((phase, i) => {
    lines.push(`**Phase ${i + 1} — ${phase.name}**`);
    for (const obj of phase.objectives) {
      lines.push(`- ${obj}`);
    }
    lines.push("");
  });
  const { body, truncated } = truncate(lines.join("\n").trim());
  return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
}

function formatQuestionLabel(q: QuestionnaireQuestion): string {
  const typeHint =
    q.type === "scale"
      ? ` (scale ${q.min ?? 1}–${q.max ?? 5})`
      : q.type === "choice" && q.options?.length
        ? ` (choice: ${q.options.join(" | ")})`
        : "";
  return `${q.label}${typeHint}`;
}

function formatAnswerValue(value: string | number): string {
  return typeof value === "number" ? String(value) : value.trim();
}

/** Serialize questionnaire questions and any hydrated responses. */
export function formatQuestionnaire(workshopId: Workshop["id"]): string {
  const content: QuestionnaireContent | undefined =
    modalContentByWorkshop[workshopId]?.questionnaire;
  if (!content) return "";

  const responses =
    useQuestionnaireResponses.getState().entriesByWorkshop[workshopId] ?? [];

  const lines: string[] = [`### Questionnaire — ${workshopId}`, "", content.intro, ""];
  for (const q of content.questions) {
    lines.push(`**Q (${q.id})** ${formatQuestionLabel(q)}`);
  }

  if (responses.length > 0) {
    lines.push("", "**Responses** (hydrated submissions):");
    for (const r of responses) {
      lines.push(
        `- **${r.displayName || r.username}** (${r.role || "role n/a"}) — submitted ${new Date(r.submittedAt).toISOString()}`,
      );
      for (const q of content.questions) {
        const ans = r.answers[q.id];
        if (ans === undefined || ans === "") continue;
        lines.push(`  - ${q.id}: ${formatAnswerValue(ans)}`);
      }
    }
  } else {
    lines.push("", "_No questionnaire responses hydrated yet._");
  }

  const { body, truncated } = truncate(lines.join("\n").trim());
  return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
}

function formatTranscriptBlocks(blocks: TranscriptBlock[]): string[] {
  const lines: string[] = [];
  for (const b of blocks) {
    if (b.kind === "banner") {
      lines.push(`[${b.time}] — ${b.label} —`);
    } else {
      const tag = b.tagText ? ` [${b.tagText}]` : "";
      lines.push(`[${b.time}] **${b.speaker}**: ${b.text}${tag}`);
    }
  }
  return lines;
}

function formatStaticTranscript(tabs: TranscriptTab[]): string {
  const lines: string[] = [];
  for (const tab of tabs) {
    lines.push(`#### ${tab.label}`);
    lines.push(...formatTranscriptBlocks(tab.blocks));
    lines.push("");
  }
  return lines.join("\n").trim();
}

function formatStoreTranscripts(workshopId: Workshop["id"]): string | null {
  const entries = useTranscripts.getState().entriesByWorkshop[workshopId] ?? [];
  if (entries.length === 0) return null;
  const lines: string[] = [];
  for (const t of entries) {
    lines.push(`#### ${t.title}`);
    if (t.body.trim()) lines.push(t.body.trim());
    if (t.mediaName) lines.push(`_Media: ${t.mediaName}_`);
    lines.push("");
  }
  return lines.join("\n").trim();
}

function formatReportContent(report: ReportContent): string {
  const lines: string[] = [
    report.footerSummary,
    "",
    "**Executive summary**",
    report.executiveSummary.purpose.body,
    "",
  ];
  for (const c of report.executiveSummary.callouts) {
    lines.push(`- **${c.title}**: ${c.body}`);
  }
  lines.push("", report.executiveSummary.contributions.body);
  lines.push("", "**Key outcomes**");
  for (const o of report.outcomes.outputs.slice(0, 6)) {
    lines.push(`- ${o.output}: ${o.description} (${o.status.text})`);
  }
  lines.push("", "**Top risks**");
  for (const r of report.risks.slice(0, 5)) {
    lines.push(`- [${r.id}] ${r.title}: ${r.body}`);
  }
  lines.push("", "**Immediate next steps**");
  for (const a of report.nextSteps.immediate.slice(0, 6)) {
    lines.push(`- ${a.title}: ${a.body} (owner: ${a.owner}, due: ${a.due})`);
  }
  lines.push(
    "",
    `**Next workshop**: ${report.nextSteps.nextWorkshopCallout.title}`,
    report.nextSteps.nextWorkshopCallout.body,
  );
  return lines.join("\n");
}

function formatAnalysisContent(analysis: AnalysisContent): string {
  const lines: string[] = ["**Session analysis summary**", ""];
  for (const k of analysis.summary.kpis) {
    lines.push(`- ${k.label}: ${k.value}${k.valueSuffix ?? ""} — ${k.sub}`);
  }
  lines.push("", "**Overview dimensions**");
  for (const row of analysis.summary.overview) {
    lines.push(`- ${row.dimension}: ${row.detail} (${row.status.text})`);
  }
  lines.push("", "**Key findings**");
  for (const f of analysis.findings.findingsTable.slice(0, 8)) {
    lines.push(`- [${f.id}] ${f.finding} → ${f.action}`);
  }
  lines.push("", "**Sentiment highlights**");
  for (const s of analysis.sentiment.sentimentTable.slice(0, 5)) {
    lines.push(`- ${s.statement}: ${s.preScore} → ${s.postScore} (${s.delta})`);
  }
  return lines.join("\n");
}

function formatDataTablesContent(data: WorkshopDataTables): string {
  const lines: string[] = [data.summary, "", "**Overview KPIs**", ""];
  for (const k of data.overviewKpis) {
    lines.push(`- ${k.label}: ${k.value} — ${k.sub}`);
  }
  lines.push("", "**Collaboration models**");
  for (const m of data.collaborationModels.slice(0, 6)) {
    lines.push(
      `- ${m.model}: human=${m.humanRole}; agent=${m.agentRole}; authority=${m.authority}`,
    );
  }
  lines.push("", "**Operating modes (sample)**");
  for (const mode of data.operatingModes.slice(0, 6)) {
    lines.push(`- ${mode.mode}: trigger=${mode.trigger}; latency=${mode.latency}`);
  }
  lines.push("", "**Performance signals (sample)**");
  for (const s of data.signalThresholds.slice(0, 5)) {
    lines.push(`- ${s.signal}: green=${s.green}; amber=${s.amber}; red=${s.red}`);
  }
  return lines.join("\n");
}

function formatPlaybackContent(playback: WorkshopPlayback): string {
  const lines: string[] = [
    `${playback.sessionTitle} — ${playback.sessionSubtitle}`,
    playback.footerSummary,
    "",
    "**Chapters**",
  ];
  for (const ch of playback.chapters) {
    lines.push(`- [${ch.timestamp}] ${ch.title} (${ch.duration}): ${ch.description}`);
  }
  lines.push("", "**Key moments**");
  for (const m of playback.keyMoments.slice(0, 10)) {
    lines.push(`- [${m.timestamp}] ${m.title}: ${m.description} (${m.tagLabel})`);
  }
  lines.push("", "**Clips**");
  for (const c of playback.clips.slice(0, 6)) {
    lines.push(`- [${c.timestamp}] ${c.title} — ${c.speaker} (${c.duration})`);
  }
  lines.push("", "_Video/binary replay media is not transcluded — chapter and moment text above is authoritative._");
  return lines.join("\n");
}

function formatCapturesContent(staticCaptures: WorkshopCaptures | null, workshopId: Workshop["id"]): string {
  const lines: string[] = [];
  if (staticCaptures) {
    lines.push(staticCaptures.summary, "", "**Session captures (catalogue)**");
    for (const c of staticCaptures.captures) {
      lines.push(
        `- [${c.timestamp}] ${c.title} (${c.kind}): ${c.caption}`,
      );
    }
  }
  const userCaptures = useCaptures.getState().entriesByWorkshop[workshopId] ?? [];
  if (userCaptures.length > 0) {
    lines.push("", "**User-added captures**");
    for (const c of userCaptures) {
      lines.push(`- ${c.title} (${c.kind}) — added ${new Date(c.addedAt).toISOString()}`);
    }
  }
  if (lines.length === 0) return "";
  lines.push("", "_Image/binary capture files are not transcluded — captions and titles above are authoritative._");
  return lines.join("\n");
}

/**
 * True when the workshop has a live (facilitator-uploaded/pasted) transcript
 * in the `useTranscripts` store — as opposed to only the static WS4 demo
 * script fixture. Used to flag the static playback fixture (and the report/
 * analysis/data-tables fixtures, when no real generated content has taken
 * their place yet — see the `useGeneratedWorkshopContent` checks in
 * `resolveOutputContent` below) as illustrative once a real transcript
 * exists, so the model doesn't conflate the programme's fixed demo
 * narrative with what was actually said in the uploaded recording.
 *
 * ADO #685 wrap-up: for report/analysis/data-tables this disclaimer is now
 * dead code once real BFF-generated content exists for the workshop — that
 * content is used verbatim, with no illustrative-fixture note, since it's
 * the real thing. Playback has no real-generated-content replacement (out
 * of scope for #685), so its fixture keeps the disclaimer unconditionally
 * whenever a live transcript exists.
 */
function hasLiveTranscript(workshopId: Workshop["id"]): boolean {
  return (useTranscripts.getState().entriesByWorkshop[workshopId] ?? []).length > 0;
}

/** Prepended to static-only output fixtures once a real transcript has been
 *  uploaded for the workshop (ADO #— transcript/fixture conflation fix). */
const DEMO_SCRIPT_NOTE =
  `_This section is the XAES Phase Zero programme's illustrative workshop` +
  ` script — fixed reference/demo content, not generated from the uploaded` +
  ` transcript below. A real transcript has been uploaded for this` +
  ` workshop; do not cite this section when answering questions about what` +
  ` was actually said in that recording — use the "transcript" output for` +
  ` that._`;

/** Prepend the demo-script disclaimer when a live transcript exists for
 *  this workshop; otherwise return `text` unchanged. */
function withDemoNoteIfLiveTranscript(workshopId: Workshop["id"], text: string): string {
  return hasLiveTranscript(workshopId) ? [DEMO_SCRIPT_NOTE, "", text].join("\n") : text;
}

/**
 * Resolve real text content for an AssetLocker output card.
 * Returns `null` when no authored content exists for that workshop/output.
 */
export function resolveOutputContent(
  workshopId: Workshop["id"],
  outputId: WorkshopOutputId,
): string | null {
  switch (outputId) {
    case "transcript": {
      const fromStore = formatStoreTranscripts(workshopId);
      if (fromStore) {
        const { body, truncated } = truncate(fromStore);
        return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
      }
      const tabs = getTranscript(workshopId);
      if (!tabs) return null;
      const { body, truncated } = truncate(formatStaticTranscript(tabs));
      return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
    }
    case "report": {
      const generated = useGeneratedWorkshopContent.getState().report[workshopId];
      if (generated) {
        const { body, truncated } = truncate(formatReportContent(generated));
        return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
      }
      if (hasLiveTranscript(workshopId)) return null;
      const report = getReport(workshopId);
      if (!report) return null;
      const { body, truncated } = truncate(formatReportContent(report));
      return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
    }
    case "analysis": {
      const generated = useGeneratedWorkshopContent.getState().analysis[workshopId];
      if (generated) {
        const { body, truncated } = truncate(formatAnalysisContent(generated));
        return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
      }
      if (hasLiveTranscript(workshopId)) return null;
      const analysis = getAnalysis(workshopId);
      if (!analysis) return null;
      const { body, truncated } = truncate(formatAnalysisContent(analysis));
      return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
    }
    case "data-tables": {
      const generated = useGeneratedWorkshopContent.getState().dataTables[workshopId];
      if (generated) {
        const { body, truncated } = truncate(formatDataTablesContent(generated));
        return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
      }
      if (hasLiveTranscript(workshopId)) return null;
      const data = getDataTables(workshopId);
      if (!data) return null;
      const { body, truncated } = truncate(formatDataTablesContent(data));
      return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
    }
    case "playback": {
      const playback = getPlayback(workshopId);
      if (!playback) return null;
      const { body, truncated } = truncate(formatPlaybackContent(playback));
      return withDemoNoteIfLiveTranscript(
        workshopId,
        [body, truncateNote(truncated)].filter(Boolean).join("\n\n"),
      );
    }
    case "captures": {
      const staticCaptures = getCaptures(workshopId);
      const text = formatCapturesContent(staticCaptures, workshopId);
      if (!text) return null;
      const { body, truncated } = truncate(text);
      return [body, truncateNote(truncated)].filter(Boolean).join("\n\n");
    }
    default:
      return null;
  }
}

const OUTPUT_IDS: WorkshopOutputId[] = [
  "transcript",
  "analysis",
  "data-tables",
  "report",
  "playback",
  "captures",
];

function collectOutputRows(workshopIds: Workshop["id"][]): string[] {
  const rows: string[] = [];
  for (const wsId of workshopIds) {
    for (const outputId of OUTPUT_IDS) {
      const content = resolveOutputContent(wsId, outputId);
      if (!content) continue;
      rows.push(`### ${wsId} · ${outputId}`, "", content, "");
    }
  }
  return rows;
}

/**
 * Output ids with resolvable content for one workshop's active chat scope —
 * fed to the BFF as `referencedOutputIds` on each chat turn so ADO #689's
 * chat-interaction telemetry can report which Asset Locker outputs a turn
 * drew on. `"MC"` (Mission Control, multi-workshop) scope isn't attributable
 * to one workshop's Analysis view, so it returns `[]`.
 */
export function getReferencedOutputIds(scope: ChatScope): WorkshopOutputId[] {
  if (scope === "MC") return [];
  return OUTPUT_IDS.filter((outputId) => resolveOutputContent(scope, outputId) !== null);
}

/**
 * Deterministic dump of workshop artefacts — pre-reading, run sequence,
 * questionnaire, and output surfaces. Injected every turn alongside
 * register dumps so the model can discuss them without @-mentions.
 */
export function buildWorkshopContentContext(scope: ChatScope): string {
  const blocks: string[] = [];
  const currentIds = currentWorkshopIds(scope);
  const priorIds = priorWorkshopIds(scope);

  // Pre-reading — current workshops always; prior workshops for continuity.
  const preReadingRows: string[] = [];
  for (const wsId of [...priorIds, ...currentIds]) {
    const text = formatPreReading(wsId);
    if (text) preReadingRows.push(`### ${wsId}`, "", text, "");
  }
  if (preReadingRows.length > 0) {
    blocks.push(
      wrapSection(
        scope === "MC" ? "Pre-reading dossiers (all workshops)" : "Pre-reading dossiers",
        `The text below is **first-party pre-reading content** from workshop dossiers` +
          ` (Doc modal). It is authoritative for briefing questions — cite by` +
          ` workshop id and section heading. Distinct from untrusted` +
          ` \`<reference>\` blocks.`,
        "pre-reading",
        preReadingRows,
      ),
    );
  }

  // Run sequence — active scope workshops (all eight for MC).
  const runSeqRows: string[] = [];
  for (const wsId of currentIds) {
    const text = formatRunSequence(wsId);
    if (text) runSeqRows.push(text, "");
  }
  if (runSeqRows.length > 0) {
    blocks.push(
      wrapSection(
        scope === "MC" ? "Run sequence (all workshops)" : "Run sequence (active workshop)",
        `The phases below are **first-party run-sequence content** from the Run` +
          ` Sequence UI — real phase names and activity objectives, not generic` +
          ` stubs. Cite by workshop id and phase number/name.`,
        "run-sequence",
        runSeqRows,
      ),
    );
  }

  // Questionnaire — current scope workshops.
  const questionnaireRows: string[] = [];
  for (const wsId of currentIds) {
    const text = formatQuestionnaire(wsId);
    if (text) questionnaireRows.push(text, "");
  }
  if (questionnaireRows.length > 0) {
    blocks.push(
      wrapSection(
        scope === "MC" ? "Questionnaires (all workshops)" : "Questionnaire (active workshop)",
        `Questions and any hydrated responses below are **first-party` +
          ` questionnaire content**. Responses appear only when the store has` +
          ` been hydrated (participant submissions in IDB / facilitation loadAll).`,
        "questionnaire",
        questionnaireRows,
      ),
    );
  }

  // Outputs — current scope only (WS4 is the primary authored set).
  const outputRows = collectOutputRows(currentIds);
  if (outputRows.length > 0) {
    blocks.push(
      wrapSection(
        scope === "MC" ? "Workshop outputs (all workshops)" : "Workshop outputs (active workshop)",
        `The content below is **first-party output artefact text** — transcript` +
          ` lines, report/analysis summaries, structured table excerpts, playback` +
          ` chapter notes, and capture captions. Binary media (video replay,` +
          ` images) is not included; do not claim to have seen it.`,
        "workshop-outputs",
        outputRows,
      ),
    );
  }

  const crewBlock = buildCrewContext(scope);
  if (crewBlock) blocks.push(crewBlock);

  return blocks.join("\n\n");
}
